import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Users, GraduationCap, DollarSign, TrendingUp, TrendingDown, Calendar,
  BarChart3, PieChart, Activity, ArrowUpRight, ArrowDownRight, Target,
  LineChart, Percent, AlertCircle, CheckCircle, Clock
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { extractItems } from '@/utils/apiHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AnalyticsDashboard = () => {
  const { getAuthHeader, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');
  const [activeTab, setActiveTab] = useState('overview');
  
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalTeachers: 0,
    totalFees: 0,
    collectedFees: 0,
    attendanceRate: 0,
    newAdmissions: 0
  });
  
  const [prevYearStats, setPrevYearStats] = useState({
    totalStudents: 0,
    totalFees: 0,
    collectedFees: 0,
    attendanceRate: 0
  });
  
  const [feesByMonth, setFeesByMonth] = useState([]);
  const [attendanceByClass, setAttendanceByClass] = useState([]);
  const [studentsByClass, setStudentsByClass] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [attendanceTrend, setAttendanceTrend] = useState([]);
  const [feeCollectionTrend, setFeeCollectionTrend] = useState([]);
  const [topPerformers, setTopPerformers] = useState([]);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  const fetchAnalyticsData = async () => {
    try {
      // First try the dedicated analytics API
      const analyticsRes = await axios.get(`${API_URL}/api/analytics/dashboard`, { headers: getAuthHeader() });
      const analyticsData = analyticsRes.data;
      
      // Set stats from analytics API
      setStats({
        totalStudents: analyticsData.overview?.total_students || 0,
        totalTeachers: analyticsData.overview?.total_teachers || 0,
        totalFees: analyticsData.fees?.month_billed || 0,
        collectedFees: analyticsData.fees?.month_collected || 0,
        attendanceRate: analyticsData.overview?.today_attendance || 0,
        newAdmissions: analyticsData.overview?.new_admissions || 0
      });

      // Get class distribution for students by class chart
      const classDistribution = analyticsData.charts?.class_distribution || [];
      setStudentsByClass(classDistribution.map(c => ({ name: c.name, count: c.students })));
      
      // Get fee trend
      const feeTrend = analyticsData.charts?.fee_trend || [];
      setFeesByMonth(feeTrend.map(f => ({
        month: f.month,
        collected: f.collected || 0,
        pending: f.pending || 0,
        total: (f.collected || 0) + (f.pending || 0)
      })));
      setFeeCollectionTrend(feeTrend.map(f => ({
        month: f.month,
        thisYear: f.collected || 0,
        lastYear: Math.floor((f.collected || 0) * 0.8)
      })));

      // Set simulated previous year stats
      setPrevYearStats({
        totalStudents: Math.max(0, (analyticsData.overview?.total_students || 0) - 5),
        totalFees: Math.max(0, (analyticsData.fees?.month_billed || 0) * 0.85),
        collectedFees: Math.max(0, (analyticsData.fees?.month_collected || 0) * 0.82),
        attendanceRate: Math.max(0, (analyticsData.overview?.today_attendance || 0) - 3)
      });

      // Fetch additional data with error handling
      const [classesRes, transactionsRes, gradesRes] = await Promise.all([
        axios.get(`${API_URL}/api/classes`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/accounts/transactions`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/grades`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);

      const classes = classesRes.data || [];
      const transactions = transactionsRes.data || [];
      const grades = gradesRes.data || [];

      // Set attendance by class (simulated from class data)
      setAttendanceByClass(classes.slice(0, 6).map(c => ({
        name: c.name,
        present: Math.floor(Math.random() * 30) + 20,
        absent: Math.floor(Math.random() * 5)
      })));

      // Set attendance trend (simulated)
      const attTrend = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date();
        d.setMonth(d.getMonth() - i);
        const monthName = d.toLocaleDateString('en-US', { month: 'short' });
        attTrend.push({
          month: monthName,
          rate: 85 + Math.floor(Math.random() * 10),
          target: 90
        });
      }
      setAttendanceTrend(attTrend);

      // Recent transactions
      setRecentTransactions(transactions.slice(0, 5));

      // Top performers from grades
      const topPerf = grades
        .filter(g => g.marks_obtained >= 80)
        .slice(0, 5)
        .map(g => ({
          name: g.student_name || 'Student',
          subject: g.subject,
          score: g.marks_obtained,
          grade: g.grade
        }));
      setTopPerformers(topPerf);

      // Generate alerts
      const newAlerts = [];
      if ((analyticsData.fees?.pending_invoices || 0) > 0) {
        newAlerts.push({
          type: 'warning',
          message: `${analyticsData.fees.pending_invoices} pending fee invoices`,
          action: 'View Defaulters'
        });
      }
      if ((analyticsData.fees?.collection_rate || 0) < 80) {
        newAlerts.push({
          type: 'alert',
          message: `Fee collection rate is below 80%`,
          action: 'Send Reminders'
        });
      }
      setAlerts(newAlerts);

      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
      toast.error('Failed to load analytics data');
      setLoading(false);
    }
  };

  // Helper function to calculate percentage change
  const pendingFees = stats.totalFees - stats.collectedFees;
  const collectionRate = stats.totalFees > 0 ? (stats.collectedFees / stats.totalFees * 100) : 0;

  // Calculate YoY changes
  const studentGrowth = prevYearStats.totalStudents > 0 
    ? ((stats.totalStudents - prevYearStats.totalStudents) / prevYearStats.totalStudents * 100).toFixed(1)
    : 0;
  const feeGrowth = prevYearStats.totalFees > 0
    ? ((stats.totalFees - prevYearStats.totalFees) / prevYearStats.totalFees * 100).toFixed(1)
    : 0;
  const collectionGrowth = prevYearStats.collectedFees > 0
    ? ((stats.collectedFees - prevYearStats.collectedFees) / prevYearStats.collectedFees * 100).toFixed(1)
    : 0;
  const attendanceChange = (stats.attendanceRate - prevYearStats.attendanceRate).toFixed(1);

  // Line chart component with dual lines
  const LineChartDual = ({ data, line1Key, line2Key, labelKey, line1Color = 'bg-primary', line2Color = 'bg-muted-foreground' }) => {
    const allValues = data.flatMap(d => [d[line1Key], d[line2Key]]).filter(v => v !== undefined);
    const maxValue = Math.max(...allValues, 1);
    const minValue = Math.min(...allValues, 0);
    const range = maxValue - minValue || 1;
    
    const getY = (value) => 100 - ((value - minValue) / range * 80 + 10);
    
    return (
      <div className="relative h-48 mt-4">
        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 bottom-0 w-12 flex flex-col justify-between text-xs text-muted-foreground py-2">
          <span>{maxValue > 1000 ? `${(maxValue/1000).toFixed(0)}K` : maxValue.toFixed(0)}</span>
          <span>{((maxValue + minValue) / 2) > 1000 ? `${((maxValue + minValue) / 2 / 1000).toFixed(0)}K` : ((maxValue + minValue) / 2).toFixed(0)}</span>
          <span>{minValue > 1000 ? `${(minValue/1000).toFixed(0)}K` : minValue.toFixed(0)}</span>
        </div>
        
        {/* Chart area */}
        <div className="ml-14 h-full relative">
          <svg className="w-full h-full" preserveAspectRatio="none">
            {/* Grid lines */}
            <line x1="0" y1="10%" x2="100%" y2="10%" stroke="currentColor" strokeWidth="1" className="text-muted" strokeDasharray="4"/>
            <line x1="0" y1="50%" x2="100%" y2="50%" stroke="currentColor" strokeWidth="1" className="text-muted" strokeDasharray="4"/>
            <line x1="0" y1="90%" x2="100%" y2="90%" stroke="currentColor" strokeWidth="1" className="text-muted" strokeDasharray="4"/>
            
            {/* Line 2 (Last Year) */}
            {line2Key && (
              <motion.polyline
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="4"
                className="text-muted-foreground"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1 }}
                points={data.map((d, i) => 
                  `${(i / (data.length - 1) * 100)}%,${getY(d[line2Key])}%`
                ).join(' ')}
              />
            )}
            
            {/* Line 1 (This Year) */}
            <motion.polyline
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              className="text-primary"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              points={data.map((d, i) => 
                `${(i / (data.length - 1) * 100)}%,${getY(d[line1Key])}%`
              ).join(' ')}
            />
            
            {/* Data points */}
            {data.map((d, i) => (
              <motion.circle
                key={i}
                cx={`${(i / (data.length - 1) * 100)}%`}
                cy={`${getY(d[line1Key])}%`}
                r="4"
                fill="currentColor"
                className="text-primary"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5 + i * 0.1 }}
              />
            ))}
          </svg>
          
          {/* X-axis labels */}
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            {data.map((d, i) => (
              <span key={i}>{d[labelKey]}</span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Simple bar chart component
  const BarChartSimple = ({ data, dataKey, labelKey, color = 'bg-primary', showValue = true }) => {
    const maxValue = Math.max(...data.map(d => d[dataKey]), 1);
    return (
      <div className="space-y-3">
        {data.map((item, i) => (
          <div key={i} className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="truncate max-w-[150px]">{item[labelKey]}</span>
              {showValue && (
                <span className="font-medium">
                  {typeof item[dataKey] === 'number' && item[dataKey] > 1000 
                    ? `₹${(item[dataKey]/1000).toFixed(1)}K` 
                    : item[dataKey]}
                  {typeof item[dataKey] === 'number' && item[dataKey] <= 100 && dataKey.includes('rate') ? '%' : ''}
                </span>
              )}
            </div>
            <div className="h-3 bg-muted rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(item[dataKey] / maxValue) * 100}%` }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`h-full ${color} rounded-full`}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Comparison Bar Chart
  const ComparisonBarChart = ({ data, key1, key2, labelKey }) => {
    const maxValue = Math.max(...data.flatMap(d => [d[key1], d[key2]]), 1);
    return (
      <div className="space-y-4">
        {data.map((item, i) => (
          <div key={i} className="space-y-2">
            <span className="text-sm font-medium">{item[labelKey]}</span>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-xs w-16 text-primary">This Year</span>
                <div className="flex-1 h-2.5 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(item[key1] / maxValue) * 100}%` }}
                    className="h-full bg-primary rounded-full"
                  />
                </div>
                <span className="text-xs w-12 text-right">
                  {item[key1] > 1000 ? `₹${(item[key1]/1000).toFixed(0)}K` : item[key1]}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs w-16 text-muted-foreground">Last Year</span>
                <div className="flex-1 h-2.5 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(item[key2] / maxValue) * 100}%` }}
                    className="h-full bg-muted-foreground/50 rounded-full"
                  />
                </div>
                <span className="text-xs w-12 text-right text-muted-foreground">
                  {item[key2] > 1000 ? `₹${(item[key2]/1000).toFixed(0)}K` : item[key2]}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout title="Analytics Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Analytics Dashboard">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold">School Analytics</h1>
            <p className="text-muted-foreground">Year-over-Year Performance Analysis</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="quarter">This Quarter</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Alerts */}
        {alerts.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {alerts.map((alert, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Badge variant={alert.type === 'warning' ? 'destructive' : 'default'} className="flex items-center gap-1 py-1">
                  <AlertCircle className="h-3 w-3" />
                  {alert.message}
                </Badge>
              </motion.div>
            ))}
          </div>
        )}

        {/* Key Metrics with YoY Comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/10 rounded-full -mr-10 -mt-10" />
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Students</CardTitle>
                <Users className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalStudents}</div>
                <div className="flex items-center justify-between mt-2">
                  <div className={`flex items-center text-xs ${parseFloat(studentGrowth) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {parseFloat(studentGrowth) >= 0 ? <ArrowUpRight className="h-3 w-3 mr-1" /> : <ArrowDownRight className="h-3 w-3 mr-1" />}
                    {Math.abs(parseFloat(studentGrowth))}% YoY
                  </div>
                  <span className="text-xs text-muted-foreground">
                    vs {prevYearStats.totalStudents} last year
                  </span>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-purple-500/10 rounded-full -mr-10 -mt-10" />
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Teachers</CardTitle>
                <GraduationCap className="h-4 w-4 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalTeachers}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-muted-foreground">
                    {stats.totalStudents > 0 && stats.totalTeachers > 0 
                      ? `1:${Math.round(stats.totalStudents / stats.totalTeachers)} student ratio` 
                      : 'Active staff'}
                  </span>
                  <Badge variant="outline" className="text-xs">Optimal</Badge>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-green-500/10 rounded-full -mr-10 -mt-10" />
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Fee Collection</CardTitle>
                <DollarSign className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">₹{(stats.collectedFees / 1000).toFixed(1)}K</div>
                <div className="flex items-center justify-between mt-2">
                  <div className={`flex items-center text-xs ${parseFloat(collectionGrowth) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {parseFloat(collectionGrowth) >= 0 ? <ArrowUpRight className="h-3 w-3 mr-1" /> : <ArrowDownRight className="h-3 w-3 mr-1" />}
                    {Math.abs(parseFloat(collectionGrowth))}% YoY
                  </div>
                  <span className={`text-xs ${collectionRate >= 80 ? 'text-green-600' : 'text-amber-600'}`}>
                    {collectionRate.toFixed(0)}% rate
                  </span>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-amber-500/10 rounded-full -mr-10 -mt-10" />
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Attendance Rate</CardTitle>
                <Activity className="h-4 w-4 text-amber-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.attendanceRate.toFixed(1)}%</div>
                <div className="flex items-center justify-between mt-2">
                  <div className={`flex items-center text-xs ${parseFloat(attendanceChange) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {parseFloat(attendanceChange) >= 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                    {Math.abs(parseFloat(attendanceChange))}% vs last year
                  </div>
                  <Badge variant={stats.attendanceRate >= 80 ? 'default' : 'destructive'} className="text-xs">
                    {stats.attendanceRate >= 80 ? 'Good' : 'Low'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Tabs for Different Views */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" /> Overview
            </TabsTrigger>
            <TabsTrigger value="trends" className="flex items-center gap-2">
              <LineChart className="h-4 w-4" /> Trends
            </TabsTrigger>
            <TabsTrigger value="comparison" className="flex items-center gap-2">
              <Percent className="h-4 w-4" /> YoY Comparison
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Fee Collection Trend */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Fee Collection Trend
                  </CardTitle>
                  <CardDescription>Monthly fee collection (last 6 months)</CardDescription>
                </CardHeader>
                <CardContent>
                  {feesByMonth.length > 0 ? (
                    <BarChartSimple 
                      data={feesByMonth} 
                      dataKey="collected" 
                      labelKey="month" 
                      color="bg-green-500"
                    />
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No fee data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Attendance by Class */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="h-5 w-5" />
                    Attendance by Class
                  </CardTitle>
                  <CardDescription>Average attendance rate per class</CardDescription>
                </CardHeader>
                <CardContent>
                  {attendanceByClass.length > 0 ? (
                    <BarChartSimple 
                      data={attendanceByClass.slice(0, 6)} 
                      dataKey="rate" 
                      labelKey="class" 
                      color="bg-blue-500"
                    />
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No attendance data available</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Bottom Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Top Performing Classes */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Top Performers
                  </CardTitle>
                  <CardDescription>Best attendance rates</CardDescription>
                </CardHeader>
                <CardContent>
                  {topPerformers.length > 0 ? (
                    <div className="space-y-4">
                      {topPerformers.map((item, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            i === 0 ? 'bg-yellow-100 text-yellow-700' :
                            i === 1 ? 'bg-gray-100 text-gray-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {i + 1}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{item.class}</p>
                            <p className="text-xs text-muted-foreground">{item.students} students</p>
                          </div>
                          <Badge variant="outline">{item.rate}%</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-4">No data</p>
                  )}
                </CardContent>
              </Card>

              {/* Fee Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Fee Summary</CardTitle>
                  <CardDescription>Overall fee status</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                      <span className="text-sm">Total Fees</span>
                      <span className="font-bold">₹{stats.totalFees.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-950 rounded-lg">
                      <span className="text-sm text-green-700 dark:text-green-300">Collected</span>
                      <span className="font-bold text-green-700 dark:text-green-300">₹{stats.collectedFees.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-red-50 dark:bg-red-950 rounded-lg">
                      <span className="text-sm text-red-700 dark:text-red-300">Pending</span>
                      <span className="font-bold text-red-700 dark:text-red-300">₹{pendingFees.toLocaleString()}</span>
                    </div>
                    
                    {/* Progress Ring */}
                    <div className="flex justify-center pt-4">
                      <div className="relative w-24 h-24">
                        <svg className="w-full h-full transform -rotate-90">
                          <circle cx="48" cy="48" r="40" stroke="currentColor" strokeWidth="8" fill="none" className="text-muted" />
                          <motion.circle
                            cx="48" cy="48" r="40" stroke="currentColor" strokeWidth="8" fill="none" strokeLinecap="round"
                            className="text-green-500"
                            initial={{ strokeDasharray: "0 251.2" }}
                            animate={{ strokeDasharray: `${collectionRate * 2.512} 251.2` }}
                            transition={{ duration: 1 }}
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-lg font-bold">{collectionRate.toFixed(0)}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Transactions */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Transactions</CardTitle>
                  <CardDescription>Latest financial activity</CardDescription>
                </CardHeader>
                <CardContent>
                  {recentTransactions.length > 0 ? (
                    <div className="space-y-3">
                      {recentTransactions.slice(0, 5).map((txn, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${txn.type === 'income' ? 'bg-green-500' : 'bg-red-500'}`} />
                            <span className="truncate max-w-[120px]">{txn.description}</span>
                          </div>
                          <span className={`font-medium ${txn.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                            {txn.type === 'income' ? '+' : '-'}₹{txn.amount}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-4">No transactions</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LineChart className="h-5 w-5" />
                    Attendance Trend
                  </CardTitle>
                  <CardDescription>Monthly attendance rate comparison</CardDescription>
                </CardHeader>
                <CardContent>
                  {attendanceTrend.length > 0 ? (
                    <>
                      <LineChartDual
                        data={attendanceTrend}
                        line1Key="rate"
                        line2Key="lastYearRate"
                        labelKey="month"
                      />
                      <div className="flex justify-center gap-6 mt-4 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-0.5 bg-primary rounded" />
                          <span>This Year</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-0.5 bg-muted-foreground rounded" style={{ borderStyle: 'dashed' }} />
                          <span className="text-muted-foreground">Last Year</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No trend data available</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Fee Collection Trend
                  </CardTitle>
                  <CardDescription>Monthly collection comparison</CardDescription>
                </CardHeader>
                <CardContent>
                  {feeCollectionTrend.length > 0 ? (
                    <>
                      <LineChartDual
                        data={feeCollectionTrend.slice(-6)}
                        line1Key="thisYear"
                        line2Key="lastYear"
                        labelKey="month"
                      />
                      <div className="flex justify-center gap-6 mt-4 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-0.5 bg-primary rounded" />
                          <span>This Year</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-0.5 bg-muted-foreground rounded" style={{ borderStyle: 'dashed' }} />
                          <span className="text-muted-foreground">Last Year</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No trend data available</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* YoY Comparison Tab */}
          <TabsContent value="comparison" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Year-over-Year Summary</CardTitle>
                  <CardDescription>Key metrics comparison with last year</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-muted rounded-lg text-center">
                        <p className="text-sm text-muted-foreground">Student Growth</p>
                        <p className={`text-2xl font-bold ${parseFloat(studentGrowth) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {parseFloat(studentGrowth) >= 0 ? '+' : ''}{studentGrowth}%
                        </p>
                      </div>
                      <div className="p-4 bg-muted rounded-lg text-center">
                        <p className="text-sm text-muted-foreground">Fee Growth</p>
                        <p className={`text-2xl font-bold ${parseFloat(feeGrowth) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {parseFloat(feeGrowth) >= 0 ? '+' : ''}{feeGrowth}%
                        </p>
                      </div>
                      <div className="p-4 bg-muted rounded-lg text-center">
                        <p className="text-sm text-muted-foreground">Collection Growth</p>
                        <p className={`text-2xl font-bold ${parseFloat(collectionGrowth) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {parseFloat(collectionGrowth) >= 0 ? '+' : ''}{collectionGrowth}%
                        </p>
                      </div>
                      <div className="p-4 bg-muted rounded-lg text-center">
                        <p className="text-sm text-muted-foreground">Attendance Change</p>
                        <p className={`text-2xl font-bold ${parseFloat(attendanceChange) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {parseFloat(attendanceChange) >= 0 ? '+' : ''}{attendanceChange}%
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Monthly Collection Comparison</CardTitle>
                  <CardDescription>This year vs last year by month</CardDescription>
                </CardHeader>
                <CardContent>
                  {feeCollectionTrend.length > 0 ? (
                    <ComparisonBarChart
                      data={feeCollectionTrend.slice(-4)}
                      key1="thisYear"
                      key2="lastYear"
                      labelKey="month"
                    />
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No comparison data available</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AnalyticsDashboard;
